import { DefaultAzureCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";
import { runAz } from "./az.js";

export interface AzAccountInfo {
  upn: string;
  tenantId: string;
  subscriptionId: string;
  subscriptionName: string;
  environmentName: string;
}

let credentialSingleton: DefaultAzureCredential | null = null;

function credential(): DefaultAzureCredential {
  return (credentialSingleton ??= new DefaultAzureCredential());
}

/**
 * Acquire a Bearer token for the m8t gateway.
 *
 * Scope: `api://<gatewayClientId>/.default`. This requires the app
 * reg to have Expose-an-API configured (identifierUris populated +
 * a user_impersonation OAuth2 permission scope + Azure CLI's
 * first-party app in preAuthorizedApplications). `deploy/setup.mjs`
 * step 1 ensures this idempotently on each run.
 *
 * Resolves audience-scoping debt from an earlier MVP implementation.
 * The CLI previously used `https://ai.azure.com/.default` (Foundry
 * audience) because Entra rejected clientId-scoped tokens without
 * Expose-an-API configured. The proxy accepts BOTH this clientId
 * audience and the Foundry audience (for backwards-compat with the
 * webapp's MSAL.js flow) — the dual-audience model is preserved.
 */
export async function getBearerToken(gatewayClientId: string): Promise<string> {
  try {
    const token = await credential().getToken(`api://${gatewayClientId}/.default`);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- @azure/identity types getToken() as non-null, but it can return null at runtime (see auth.test.ts)
    if (!token) {
      throw new LocalCliError({
        code: "AUTH_NULL_TOKEN",
        message: "Azure returned no token despite no error being raised.",
      });
    }
    return token.token;
  } catch (e) {
    if (e instanceof LocalCliError) throw e;
    throw translateAzureIdentityError(e);
  }
}

/** Acquire a data-plane token for the Foundry project API (audience ai.azure.com). */
export async function getFoundryToken(): Promise<string> {
  const token = await credential().getToken("https://ai.azure.com/.default");
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- @azure/identity types getToken() as non-null, but it can return null at runtime (see auth.test.ts)
  if (!token) {
    throw new LocalCliError({ code: "AUTH_NULL_TOKEN", message: "Azure returned no token." });
  }
  return token.token;
}

/**
 * Shell out to `az account show --output json` to fetch the active
 * subscription's identity info. Used by `m8t whoami` (and by discovery
 * for the active subscription ID).
 *
 * Use `az account show` (no leading double-dash) —
 * `az --account` is not a thing, and `az --version` ignores `--output json`.
 */
export async function getAzAccount(): Promise<AzAccountInfo> {
  const json = await runAz(["account", "show", "--output", "json"]);
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(json) as Record<string, unknown>;
  } catch (e) {
    throw new LocalCliError({
      code: "AZ_OUTPUT_INVALID",
      message: `Failed to parse 'az account show' output: ${(e as Error).message}`,
      hint: "Try 'az account show --output json' manually to investigate.",
    });
  }

  const user = parsed.user as Record<string, unknown> | undefined;
  return {
    upn: typeof user?.name === "string" ? user.name : "",
    tenantId: typeof parsed.tenantId === "string" ? parsed.tenantId : "",
    subscriptionId: typeof parsed.id === "string" ? parsed.id : "",
    subscriptionName: typeof parsed.name === "string" ? parsed.name : "",
    environmentName: typeof parsed.environmentName === "string" ? parsed.environmentName : "",
  };
}

/**
 * The signed-in principal's object id (oid), decoded from an ARM access
 * token. `az ad signed-in-user show` fails for personal MS accounts, so we
 * read the oid claim directly (no verification needed — it's our own token).
 */
export async function getCallerObjectId(): Promise<string> {
  const token = await credential().getToken("https://management.azure.com/.default");
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- @azure/identity types getToken() as non-null, but it can return null at runtime (see auth.test.ts)
  if (!token) {
    throw new LocalCliError({ code: "AUTH_NULL_TOKEN", message: "Azure returned no token." });
  }
  const payload = token.token.split(".")[1] ?? "";
  const json = Buffer.from(payload + "=".repeat((4 - (payload.length % 4)) % 4), "base64").toString("utf8");
  const oid = (JSON.parse(json) as { oid?: string }).oid;
  if (!oid) {
    throw new LocalCliError({
      code: "AUTH_NO_OID",
      message: "Could not determine your object id from the Azure token.",
      hint: "Run 'az login' and retry.",
    });
  }
  return oid;
}

function translateAzureIdentityError(e: unknown): LocalCliError {
  const name = e instanceof Error ? e.name : "";
  const message = e instanceof Error ? e.message : String(e);
  if (name === "CredentialUnavailableError" || /credentialunavailable/i.test(message)) {
    return new LocalCliError({
      code: "AUTH_NOT_SIGNED_IN",
      message: "You are not signed in to Azure.",
      hint: "Run 'az login' to sign in.",
      cause: e,
    });
  }
  if (name === "AuthenticationRequiredError" || /interactive_required|invalid_grant/i.test(message)) {
    return new LocalCliError({
      code: "AUTH_EXPIRED",
      message: "Your Azure login has expired or requires re-authentication.",
      hint: "Run 'az login' to re-authenticate.",
      cause: e,
    });
  }
  if (/aadsts50059|tenant/i.test(message)) {
    return new LocalCliError({
      code: "AUTH_WRONG_TENANT",
      message: `Your active Azure session may be in a different tenant: ${message}`,
      hint: "Run 'az login --tenant <gateway-tenant-id>' to switch.",
      cause: e,
    });
  }
  return new LocalCliError({
    code: "AUTH_FAILED",
    message: `Azure token acquisition failed: ${message}`,
    cause: e,
  });
}
